import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon, ArrowRight, Sparkles, TrendingDown, MapPin, ScanLine } from "lucide-react";
import { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const PERKS = [
  [TrendingDown, "Compare grocers near you, anywhere in Canada"],
  [MapPin, "One smart route, straight to Google or Apple Maps"],
  [ScanLine, "Snap your receipt — see what you really saved"],
];

const BrandMark = ({ light }) => (
  <div className="flex items-center gap-3" data-testid="login-logo">
    <img src="/pantry-pilot-logo.png" alt="Pantry Pilot" className="h-14 w-14 rounded-2xl pp-shadow-lg object-cover" />
    <div className="leading-none">
      <p className={`text-2xl font-extrabold tracking-tight ${light ? "text-white" : "text-pp-text"}`}>Pantry<span className={light ? "text-pp-gold" : "text-pp-forest"}>Pilot</span></p>
      <p className={`text-xs font-semibold mt-1 ${light ? "text-white/75" : "text-pp-muted"}`}>Smarter grocery runs · free forever</p>
    </div>
  </div>
);

export default function Login() {
  const { login, register, guest } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const go = (u) => navigate(u.onboarded ? "/" : "/onboarding", { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password, remember) : await register(form.email, form.password, form.name || "Shopper");
      toast.success(`Welcome${u.name ? ", " + u.name.split(" ")[0] : ""}!`);
      go(u);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not sign in");
    } finally { setBusy(false); }
  };

  const demo = async () => {
    setBusy(true);
    try { const u = await login("demo@pantrypilot.ca", "demo1234", remember); toast.success("Signed in as Demo Shopper"); go(u); }
    catch { toast.error("Demo login failed"); } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const asGuest = async () => {
    setBusy(true);
    try { go(await guest()); } catch { toast.error("Could not start guest session"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[1.1fr_1fr] bg-pp-cream">
      {/* Visual panel */}
      <div className="relative h-64 sm:h-80 lg:h-auto overflow-hidden">
        <img src="/hero-groceries.jpg" alt="Fresh groceries and a phone showing a shopping route" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#075B3A]/95 via-[#075B3A]/55 to-black/10" />
        <div className="relative z-10 h-full flex flex-col justify-between p-6 sm:p-10">
          <div className="hidden lg:block"><BrandMark light /></div>
          <div className="max-w-lg mt-auto">
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight text-white">
              Your groceries. Your route.<br /><span className="text-pp-gold">Your best price.</span>
            </h1>
            <ul className="mt-5 space-y-2.5 hidden sm:block">
              {PERKS.map(([Icon, t]) => (
                <li key={t} className="flex items-center gap-3 text-white/90 text-sm font-semibold">
                  <span className="grid place-items-center h-8 w-8 rounded-xl bg-white/15 backdrop-blur-md"><Icon className="h-4 w-4 text-pp-gold" /></span>{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-start lg:items-center justify-center px-5 sm:px-10 pb-10 -mt-10 lg:mt-0 relative z-20">
        <div className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 pp-shadow-lg pp-fade-up">
          <div className="mb-6"><BrandMark /></div>
          <h2 className="text-2xl font-extrabold text-pp-text" data-testid="auth-title">{mode === "login" ? "Welcome back" : "Create your free account"}</h2>
          <p className="text-pp-muted mt-1 text-sm">{mode === "login" ? "Sign in to plan your next trip." : "Start saving on every grocery run — no cost, ever."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-3.5 h-4 w-4 text-pp-muted" />
                  <Input id="name" data-testid="auth-name" className="pl-9 h-12 bg-pp-cream/60 rounded-xl" value={form.name} onChange={set("name")} placeholder="Jane Shopper" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3.5 h-4 w-4 text-pp-muted" />
                <Input id="email" type="email" required autoComplete="email" data-testid="auth-email" className="pl-9 h-12 bg-pp-cream/60 rounded-xl" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3.5 h-4 w-4 text-pp-muted" />
                <Input id="password" type="password" required autoComplete={mode === "login" ? "current-password" : "new-password"} data-testid="auth-password" className="pl-9 h-12 bg-pp-cream/60 rounded-xl" value={form.password} onChange={set("password")} placeholder="••••••••" />
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-sm font-medium text-pp-text cursor-pointer select-none">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="stay-signed-in-checkbox" />
              Stay signed in on this device
            </label>
            <Button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full h-12 rounded-full bg-pp-forest hover:bg-pp-forest/90 text-base font-bold transition-transform duration-150 hover:scale-[1.01] active:scale-[0.98]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Sign in" : "Create account")}
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-pp-muted">
            <div className="h-px flex-1 bg-pp-border" /> or <div className="h-px flex-1 bg-pp-border" />
          </div>

          <div className="grid sm:grid-cols-2 gap-2.5">
            <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="h-11 rounded-full bg-white font-semibold">
              <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Google
            </Button>
            <Button variant="outline" onClick={asGuest} data-testid="guest-login" className="h-11 rounded-full bg-white font-semibold text-pp-text">
              Continue as guest <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <button onClick={demo} data-testid="demo-login"
            className="mt-4 w-full flex items-center justify-center gap-1.5 rounded-full bg-pp-gold/20 py-2.5 text-sm font-bold text-[#7A5300] transition-colors hover:bg-pp-gold/30">
            <Sparkles className="h-4 w-4" /> Try the demo account
          </button>

          <p className="mt-6 text-center text-sm text-pp-muted">
            {mode === "login" ? "New to Pantry Pilot?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold text-pp-forest hover:underline">
              {mode === "login" ? "Create a free account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
